import * as React from 'react';
import { FunctionComponent, useState } from 'react';
import moment from 'moment';
import propz from 'propz';
import { AppUser } from 'Src/views/App/App';
import { Tournament } from '../../models/tournament';
import { TOURNAMENT_SUBTYPE } from '../../consts/tournament';
import { EVENT_STATUS_SERVER_TO_CLIENT_MAPPING } from '../../consts/event';
import { KIND } from '../../consts/school';
import { isTeamOrTwoOnTwoSportEvent } from '../../helpers/event/event';
import {
  isIndividualTournament,
  isSingleDoubleTournament,
  isTeamTournament
} from '../../helpers/tournament/tournament';
import { formatTournamentAges } from '../../helpers/ageGroups';
import { SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from '../../consts/common';
import { SchoolEvent } from '../../models/event';
import { Button } from '../Button/Button';
import { assignTournamentEventNumbers } from '../../helpers/service/adminSU/tournamentEventsTable';

interface Props {
  tournament: Tournament;
  events: SchoolEvent[];
  user: AppUser;
  onNumbersAssigned?: () => void;
}

export const TournamentEventsSummary: FunctionComponent<Props> = props => {
  const { user, events, tournament } = props;
  const [startFrom, setStartFrom] = useState(1);
  const [isAssigning, setIsAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const getPlayersName = event => {
    let playerNameList = [];
    const activeSchoolId = user.activeSchoolId;
    if (isTeamOrTwoOnTwoSportEvent(event)) {
      const team = event.teamsData.find(team => team.schoolId === activeSchoolId);
      if (typeof team !== 'undefined') {
        playerNameList = team.players.map(player => {
          const firstName = propz.get(player, ['firstName'], ''),
            lastName = propz.get(player, ['lastName'], '');

          return `${firstName} ${lastName}`;
        });
      } else {
        console.error(`Can not find team with school id: ${activeSchoolId}`);
      }
    } else {
      playerNameList = event.individualsData
        .filter(individual => {
          const individualSchoolId = individual.schoolId;
          const activeSchool = event.invitedSchools.find(invitedSchool => invitedSchool.id === activeSchoolId);
          const activeSchoolKind = propz.get(activeSchool, ['kind']);
          const isActiveSchoolSchoolUnion = activeSchoolKind === KIND.SCHOOL_UNION;

          if (isActiveSchoolSchoolUnion) {
            const schoolUnionSchoolIds = activeSchool.schoolIds;
            return schoolUnionSchoolIds.some(schoolId => schoolId === individualSchoolId);
          } else {
            return individualSchoolId === activeSchoolId;
          }
        })
        .map(individual => {
          const firstName = propz.get(individual, ['firstName'], ''),
            lastName = propz.get(individual, ['lastName'], '');

          return `${firstName} ${lastName}`;
        });
    }

    return `${playerNameList.join('\n ')}`;
  };

  const { subType } = tournament;
  const isSailing = subType === TOURNAMENT_SUBTYPE.SAILING;
  const isKindSchool = user.activeSchool.kind === KIND.SCHOOL;
  const isKindSchoolUnion = user.activeSchool.kind === KIND.SCHOOL_UNION;

  const isShowGroupName = (isTeamTournament(tournament) || isSingleDoubleTournament(tournament)) && !isSailing;
  const isShowPlayers = isKindSchool && isIndividualTournament(tournament);
  const isShowNumber = isKindSchoolUnion && isIndividualTournament(tournament);

  const rows = events.map((event, index) => {
    const { status, gender: eventGender } = event;
    const sportName = propz.get(event, ['sport', 'name'], '');

    const statusEvent = status ? EVENT_STATUS_SERVER_TO_CLIENT_MAPPING[event.status] : '';
    const eventName = propz.get(event, ['generatedNames', user.activeSchoolId], '');
    const gender = eventGender ? SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[event.gender] : '';
    const eventNumber = propz.get(event, ['quantumEventNumber'], '');
    const eventAges = formatTournamentAges(event.ages);

    let groupName;
    const groupId = propz.get(event, ['tournamentGroupId'], undefined);
    if (typeof groupId === 'undefined') {
      groupName = '';
    } else {
      const group = tournament.customGroups.find(customGroup => customGroup.id === groupId);
      if (typeof group !== 'undefined') {
        groupName = group.name;
      } else {
        console.error(`Can not find group with id: ${groupId}`);
        groupName = '';
      }
    }

    return (
      <tr key={`tournament_events_summary_${index}`}>
        <td>{index + 1}</td>

        {isShowNumber && <td>{eventNumber}</td>}
        <td>{sportName}</td>
        <td>{eventName}</td>
        <td>{eventAges}</td>

        {isShowPlayers && <td>{getPlayersName(event)}</td>}
        <td>{gender}</td>

        {isShowGroupName && <td>{groupName}</td>}
        <td>{moment(event.startTime).format('DD-MM-YYYY HH:mm')}</td>
        <td>{statusEvent}</td>
      </tr>
    );
  });

  const emptyRow = (
    <tr key={'events_empty_row'}>
      <td>No events</td>
    </tr>
  );

  const assignNumbers = () => {
    if (!Number.isInteger(Number(startFrom)) || Number(startFrom) < 0) {
      return setAssignError('Starting number must be a non-negative whole number.');
    }
    setIsAssigning(true);
    setAssignError('');
    return assignTournamentEventNumbers(user, tournament.id, Number(startFrom))
      .then(() => props.onNumbersAssigned && props.onNumbersAssigned())
      .catch(error => setAssignError((((error || {}).response || {}).data || {}).details
        ? error.response.data.details.text : 'Unable to assign event numbers.'))
      .then(() => setIsAssigning(false));
  };

  return (
    <>
    {isShowNumber && <div className="d-flex align-items-end mt-3 mb-3">
      <label className="mb-0 mr-2">Event number from
        <input className="form-control" type="number" min="0" value={startFrom}
          onChange={event => setStartFrom(Number(event.target.value))} />
      </label>
      <Button text={isAssigning ? 'Assigning…' : 'Assign event numbers'} disabled={isAssigning} onClick={assignNumbers} />
    </div>}
    {assignError && <div className="alert alert-danger">{assignError}</div>}
    <table className={'table table-sm mPre'}>
      <thead>
        <tr>
          <th />

          {isShowNumber && <th>Number</th>}
          <th>Sport</th>
          <th>Event</th>
          <th>Age</th>

          {isShowPlayers && <th>Players</th>}
          <th>Gender</th>

          {isShowGroupName && <th>Group name</th>}
          <th>Start time</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>{rows.length > 0 ? rows : emptyRow}</tbody>
    </table>
    </>
  );
};

TournamentEventsSummary.displayName = 'TournamentEventsSummary';



